import { Link, useLocation } from "react-router-dom";
import SEO from "../components/SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import PageHeader from "../components/PageHeader";
import { seoConfig } from "../data/seoConfig";
import searchConsoleIndexTargets from "../data/searchConsoleIndexTargets.json";
import { getLocalizedPriorityPage } from "../data/localizedPriorityPages";
import {
  getSearchConsoleHubAlternates,
  getSearchConsoleHubPage,
  searchConsoleEditionHubs,
} from "../data/searchConsoleIndexHubs";

const englishHubs = [
  { path: "/ai", label: "AI guides" },
  { path: "/articles", label: "Robotics articles" },
  { path: "/guides", label: "Learning library" },
  { path: "/robotics", label: "Robotics guides" },
  { path: "/humanoid-robots", label: "Humanoid robots" },
  { path: "/robotics-news", label: "Robotics news" },
];

const localizedHomeLabels = {
  de: "Startseite",
  fr: "Accueil",
  ja: "ホーム",
  "zh-cn": "首页",
};

const peerSectionLabels = {
  en: { title: "Browse another library", open: "Open library" },
  de: { title: "Weitere Bereiche", open: "Bereich öffnen" },
  fr: { title: "Explorer une autre rubrique", open: "Ouvrir la rubrique" },
  ja: { title: "別のカテゴリを見る", open: "カテゴリを開く" },
  "zh-cn": { title: "浏览其他栏目", open: "打开栏目" },
};

function humanizePath(path) {
  return path
    .split("/")
    .filter(Boolean)
    .at(-1)
    .split("-")
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");
}

export default function SearchConsoleHubPage() {
  const location = useLocation();
  const page = getSearchConsoleHubPage(location.pathname);
  if (!page) return null;

  const targets = page.curatedLinks?.map((item) => item.path) || searchConsoleIndexTargets[page.path] || [];
  const homePath = page.locale === "en" ? "/" : `/${page.locale}`;
  const homeLabel = page.locale === "en" ? "Home" : localizedHomeLabels[page.locale];
  const breadcrumbs = [
    { label: homeLabel, to: homePath },
    { label: page.heading, to: page.path },
  ];
  const peers = page.locale === "en"
    ? englishHubs
    : (searchConsoleEditionHubs[homePath] || []).map((path) => {
        const hub = getSearchConsoleHubPage(path);
        const priority = getLocalizedPriorityPage(path)?.page;
        return { path, label: hub?.heading || priority?.title?.[page.locale] || humanizePath(path) };
      });
  const labels = peerSectionLabels[page.locale] || peerSectionLabels.en;
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: page.heading,
    description: page.description,
    url: `${seoConfig.baseUrl}${page.path}`,
    inLanguage: page.lang,
    dateModified: page.dateModified || "2026-08-10",
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: targets.length,
      itemListElement: targets.map((path, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: page.curatedLinks?.find((item) => item.path === path)?.label || humanizePath(path),
        url: `${seoConfig.baseUrl}${path}`,
      })),
    },
  };

  return (
    <div lang={page.lang}>
      <SEO
        title={page.title}
        description={page.description}
        path={page.path}
        type="website"
        lang={page.lang}
        modifiedTime={page.dateModified || "2026-08-10"}
        alternates={getSearchConsoleHubAlternates(page.path)}
        schema={[buildBreadcrumbSchema(breadcrumbs), itemListSchema]}
      />
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        eyebrow={page.eyebrow}
        title={page.heading}
        description={page.description}
      />

      <section className="site-shell py-10 sm:py-14">
        <div className="grid gap-5 lg:grid-cols-[1.2fr_.8fr]">
          <article className="article-panel">
            <p className="text-lg font-semibold leading-9 text-neutral-800">{page.intro}</p>
          </article>
          <aside className="rounded-[2rem] border border-neutral-200 bg-neutral-950 p-6 text-white shadow-sm">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-300">{page.localeName}</p>
            <p className="mt-4 text-5xl font-black">{targets.length}</p>
            <p className="mt-2 text-sm leading-7 text-neutral-300">{page.directoryHeading}</p>
          </aside>
        </div>
      </section>

      {page.curatedLinks?.length > 0 && (
        <section className="site-shell pb-10" aria-labelledby="guide-starting-points">
          <h2 id="guide-starting-points" className="text-2xl font-black text-neutral-950">{page.directoryHeading}</h2>
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            {page.curatedLinks.map((item) => (
              <article key={item.path} className="soft-card p-6">
                <h3 className="text-xl font-black"><Link to={item.path} className="text-blue-700 underline underline-offset-4 hover:text-blue-900">{item.label}</Link></h3>
                <p className="mt-3 text-base leading-8 text-neutral-700">{item.description}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="site-shell pb-12 sm:pb-16">
        <div className="rounded-[2rem] border border-neutral-200 bg-white p-6 shadow-sm sm:p-8">
          <h2 className="text-2xl font-black tracking-tight text-neutral-950">{labels.title}</h2>
          <div className="mt-6 flex flex-wrap gap-3">
            {peers.filter((item) => item.path !== page.path).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="rounded-full border border-neutral-200 bg-neutral-50 px-4 py-2 text-sm font-black text-neutral-800 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                aria-label={`${labels.open} ${item.label}`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
